import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get, memoize } from 'lodash';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { P200 } from '@atlaskit/theme/colors';
import { ColGroup, HeadCell, SearchResultColumn, TableBody, TableHead } from '@css-xp/base';
import { CustomDynamicResultTable } from '@css-xp/base/custom-dynamic-result-table';
import { TermDetailsAccount, TermDetailsAccountConnection } from '@css-xp/graphtypes/src/generated-types-new';
import {
  FormatCurrency,
  NoData,
  TableHeading,
  emptyStateWithDescriptionProp,
  getSelectedColumns,
} from '@css-xp/ppc-common';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@css-xp/ppc-constants';
import './associated-billing-accounts.scss';

interface AssociatedBillingAccountPorps {
  associatedBillingAccounts: TermDetailsAccountConnection | undefined | null;
  isTermPaymentDataLoading?: boolean;
  errorFetchingTermPaymentData?: string;
}

const enum BillingAccountsColumns {
  COMPANY_NAME = 'Company name',
  CREDIT_LIMIT_CONSUMED = 'Credit limit consumed',
}

const getDataForCurrentPage = (currentPage: number, pageSize: number, data: TermDetailsAccount[]) => {
  if (!data || data.length === 0) return [];
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return data?.slice(start, end);
};

export const AssociatedBillingAccountsTablePPC = ({
  associatedBillingAccounts,
  errorFetchingTermPaymentData,
  isTermPaymentDataLoading,
}: AssociatedBillingAccountPorps) => {
  const [tableData, setTableData] = useState<{
    data: TermDetailsAccount[];
    currentPage: number;
    rowsPerPage: number;
  }>({
    data: [],
    currentPage: DEFAULT_PAGE,
    rowsPerPage: DEFAULT_PAGE_SIZE,
  });

  const totalCount = associatedBillingAccounts?.totalCount;
  const associatedBillingAccountsList = useMemo(() => {
    return get(associatedBillingAccounts, 'nodes', []);
  }, [associatedBillingAccounts]);

  useEffect(() => {
    setTableData({
      ...tableData,
      currentPage: 1,
      data: getDataForCurrentPage(1, tableData.rowsPerPage, associatedBillingAccountsList as TermDetailsAccount[]),
    });
  }, [associatedBillingAccountsList]);

  const getHeaders = memoize(() => {
    return [
      {
        name: BillingAccountsColumns.COMPANY_NAME,
        content: BillingAccountsColumns.COMPANY_NAME,
        width: 40,
      },
      {
        name: BillingAccountsColumns.CREDIT_LIMIT_CONSUMED,
        content: BillingAccountsColumns.CREDIT_LIMIT_CONSUMED,
        width: 40,
      },
    ];
  });

  const getBillingAccountsData = (item: TermDetailsAccount) => {
    const columns: (HeadCell & SearchResultColumn)[] = getSelectedColumns(getHeaders());
    const rowData = columns.map((columns) => {
      const { creditOutstanding, customerName } = item;
      switch (columns.name) {
        case BillingAccountsColumns.COMPANY_NAME:
          return (
            <td>
              <span>{customerName ?? 'Billing account'}</span>
            </td>
          );
        case BillingAccountsColumns.CREDIT_LIMIT_CONSUMED:
          return (
            <td>
              {creditOutstanding ? (
                <FormatCurrency
                  value={creditOutstanding}
                  currency={associatedBillingAccounts?.currency}
                  showCurrencyCode
                  showCommas
                />
              ) : (
                <NoData />
              )}
            </td>
          );
      }
    });
    return rowData;
  };

  const renderTable = () => {
    const nodes = tableData.data;
    const children = (
      <>
        <ColGroup columns={getSelectedColumns(getHeaders())} />
        <TableHead columns={getSelectedColumns(getHeaders())} />
        <TableBody>
          {nodes?.map((item: TermDetailsAccount, index: number) => {
            return <tr key={index}> {getBillingAccountsData(item).map((node) => node)}</tr>;
          })}
        </TableBody>
      </>
    );
    return children;
  };

  const handleRowChange = useCallback(
    (rowCount: number) => {
      setTableData({
        ...tableData,
        currentPage: 1,
        rowsPerPage: rowCount,
        data: getDataForCurrentPage(1, rowCount, associatedBillingAccountsList as TermDetailsAccount[]),
      });
    },
    [tableData]
  );

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      setTableData({
        ...tableData,
        currentPage: pageNumber,
        data: getDataForCurrentPage(
          pageNumber,
          tableData.rowsPerPage,
          associatedBillingAccountsList as TermDetailsAccount[]
        ),
      });
    },
    [tableData]
  );

  const getInnerHeading = () => {
    const label = 'Associated billing accounts';
    return (
      <>
        <TableHeading>{label}</TableHeading>
        <div className="associated-accounts-inner-heading-content">
          <InfoIcon label="" size="medium" primaryColor={P200} />
          <p className="associated-accounts-inner-heading-text">
            Net-30 payment terms have multiple associated billing accounts and the credits utilised across them are
            highlighted below
          </p>
        </div>
      </>
    );
  };

  return (
    <div className="ppc-associated-accounts-container">
      <CustomDynamicResultTable
        isLoading={isTermPaymentDataLoading ? isTermPaymentDataLoading : false}
        error={errorFetchingTermPaymentData}
        noResults={totalCount === 0}
        totalCount={totalCount ? totalCount : 0}
        isPaginationEnabled={totalCount ? totalCount > DEFAULT_PAGE_SIZE : false}
        emptyStateProps={emptyStateWithDescriptionProp}
        currentPage={tableData.currentPage}
        rowsPerPage={tableData.rowsPerPage}
        onPageChange={handlePageChange}
        onRowChange={handleRowChange}
        innerHeading={getInnerHeading()}
        testId="ppc-associated-accounts"
      >
        {renderTable()}
      </CustomDynamicResultTable>
    </div>
  );
};
